import {when} from 'redux-when';
import {
  ACCOUNT_TYPE_RELATIVE,
  ACCOUNT_TYPE_SENIOR,
  MESSAGE_IMAGE_TAG,
  MESSAGE_TYPING_WAIT
} from "../constants";
import {
  MESSAGE_ACCEPT,
  MESSAGE_FINISH,
  MESSAGE_MISS,
  MESSAGE_REPLY_NOTIFICATION,
  MESSAGE_SEND,
  MESSAGE_START,
  MESSAGE_TYPING,
  MESSAGE_TYPING_START,
  MESSAGE_TYPING_STOP
} from "../constants/actionTypes";
import {
  RC_NOTIFICATION,
  RC_TYPING_RECEIVE
} from "../../../shared/modules/rocketChat/constants/actionTypes";
import * as rocketChatAction
  from "../../../shared/modules/rocketChat/actions/rocketChatAction";
import {
  b64EncodeUnicode,
  getStorageImageUrl,
  hash
} from "../../../shared/actions";
import * as notificationAction
  from "../../../shared/modules/notification/actions/notificationsAction";
import {NOTIFICATION_ERROR} from "../../../shared/modules/notification/constants";
import {STORAGE_UPLOAD_URL} from "../constants/api";
import {
  IMAGE_PERMISSION_R,
  IMAGE_PERMISSION_RWE
} from "../../../shared/constants/image";
import {requestAuth} from "../../../request";
import { sendAnalyticsEvent } from '../../../shared/actions/analytics';
import { ANALYTICS_SPECS } from '../../../shared/constants';

/**
 * Gets user room
 * @param {string} user - room name based on direct message user identification
 * @returns {function(dispatch, getState)} thunk to be dispatched for Room object
 */
export function getRoom(user) {
  return (dispatch, getState) => {
    return getState().rocketChat.rooms[user];
  }
}

/**
 * Sends a message
 * @returns {function(dispatch, getState): Promise<any>} promised thunk to be dispatched
 */
export function send() {
  sendAnalyticsEvent(ANALYTICS_SPECS.MESSAGING.NAME, ANALYTICS_SPECS.MESSAGING.EVENTS.SEND_MESSAGE);
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      const {user, text} = getState().message;

      if (text.length) {
        resolve(
          dispatch(rocketChatAction.sendDirectMessage(user, text))
            .then(() => {
              return dispatch(
                {
                  type: MESSAGE_SEND,
                  user: user,
                  room: dispatch(getRoom(user)).rid,
                  message: text
                }
              );
            })
        );
      }
    })
  };
}

/**
 * Reply from app local notification
 * @param {string} userId - user identification with prefix (f.e. device_user_111111)
 * @param {string} text - text message
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function reply(userId, text) {
  sendAnalyticsEvent(ANALYTICS_SPECS.MESSAGING.NAME, ANALYTICS_SPECS.MESSAGING.EVENTS.SEND_MESSAGE);
  return (dispatch) => {
    dispatch(
      {
        type: MESSAGE_REPLY_NOTIFICATION,
        user: userId,
        text: text
      }
    );
    dispatch(send());
  };
}

/**
 * Typing a message
 * NB! Logged in user is typing
 * @param {string} text - text that was typed
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function typing(text) {
  return (dispatch, getState) => {
    return dispatch(rocketChatAction.typing(getState().message.user))
      .then(() => {
        return dispatch(
          {
            type: MESSAGE_TYPING,
            text: text
          }
        );
      });
  };
}

/**
 * Gets history for a user
 * @param {string} user - username
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function getHistory(user) {
  return (dispatch) => dispatch(rocketChatAction.getHistory(user));
}

/**
 * Sets history as being read
 * @param {string} user - username
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function setHistoryRead(user) {
  return (dispatch) => {
    return Promise.resolve(dispatch(rocketChatAction.setHistoryRead(user)));
  }
}

/**
 * Track other user typing
 * @param {string} user - user identification
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function trackTyping(user) {
  return (dispatch, getState) => {
    dispatch(
      when(
        (state, action) => {
          return action.type === RC_TYPING_RECEIVE &&
            state.rocketChat.typings[user]
        },
        () => {
          setTimeout(() => {
            if (getState().message.isTyping) {
              dispatch({type: MESSAGE_TYPING_STOP});
            }
          }, MESSAGE_TYPING_WAIT);
          return {type: MESSAGE_TYPING_START}
        }
      )
    );
  }
}

/**
 * Processes notification based on it`s index
 * @param {int} index - notification index
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function processNotification(index) {
  return (dispatch) => {
    dispatch(
      when(
        (state, action) => {
          return action.type === RC_NOTIFICATION &&
            state.rocketChat.notifications
              .some((notification) => notification.isAtChatStream(state.message.user));
        },
        () => rocketChatAction.checkNotification(index)
      )
    );
  }
}

/**
 * Triggers after we gets into a user room
 * Either accept or start a message
 * @param {string} user - username
 * @param {int} notification - index of a notification
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function onRoom(user, notification = 0) {
  return (dispatch) => {
    return dispatch(getHistory(user))
      .then(() => {
        dispatch(setHistoryRead(user));
        // (kkashpur) For now we do NOT track typing `dispatch(trackTyping(user));`
        dispatch(processNotification(notification));
      })
  };
}

/**
 * Accepts a message
 * @param {object} notification message
 * @returns {function(dispatch, getState): Promise<any>} promised thunk to be dispatched
 */
export function accept(notification) {
  return (dispatch, getState) => {
    const index = getState().rocketChat.notifications.indexOf(notification);
    const user = notification.room;

    return dispatch(onRoom(user, index))
      .then(() => {
        dispatch({type: MESSAGE_ACCEPT, user: user});

        return dispatch(rocketChatAction.checkNotification(index));
      });
  }
}

/**
 * Declines new message
 * @param {object} notification - notification
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function decline(notification) {
  return (dispatch, getState) => {
    const index = getState().rocketChat.notifications.indexOf(notification);

    dispatch(rocketChatAction.checkNotification(index));
  }
}

/**
 * Starts a conversation
 * @param {string} user - username
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function start(user) {
  return (dispatch) => {
    return dispatch(onRoom(user))
      .then(() => {
        return dispatch(
          {
            type: MESSAGE_START,
            user: user
          }
        );
      });
  }
}

/**
 * Misses a conversation
 * @param {string} user - username
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function miss(user) {
  return (dispatch) => {
    return dispatch(
      {
        type: MESSAGE_MISS,
        user: user
      }
    );
  };
}

/**
 * Finishes a conversation
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function finish() {
  return (dispatch) => {
    return dispatch({type: MESSAGE_FINISH});
  };
}

/**
 * Send a image through the message stream
 * @param {object} upload - file and url
 * @param {object} senior - target senior
 * @returns {function(dispatch, getState) : Promise<any>} promised thunk to be dispatched
 */
export function sendImage(upload, senior) {
  sendAnalyticsEvent(ANALYTICS_SPECS.MESSAGING.NAME, ANALYTICS_SPECS.MESSAGING.EVENTS.SEND_PICTURE);
  return (dispatch, getState) => {
    const { user } = getState().message;
    const rights = [
      {
        account_type: ACCOUNT_TYPE_RELATIVE,
        account_id: user.cust_id,
        rights: IMAGE_PERMISSION_RWE
      },
      {
        account_type: ACCOUNT_TYPE_SENIOR,
        account_id: senior.device_id,
        rights: IMAGE_PERMISSION_R
      }
    ];

    return requestAuth()
      .post(STORAGE_UPLOAD_URL)
      .set("Rights", JSON.stringify(rights))
      .attach("uploadfile", upload.file, b64EncodeUnicode(upload.file.name))
      .on("error", () => {
        dispatch(
          notificationAction.addNotification(
            __("Uploading the image failed."),
            NOTIFICATION_ERROR
          )
        )
      })
      .then(({ body }) => {
        const messageTag = `${MESSAGE_IMAGE_TAG}${body.hash}`;

        return dispatch(rocketChatAction.sendDirectMessage(user, messageTag));
      });
  };
}

/**
 * Send a image through the message stream
 * @param {string} text - the message text
 * @returns {object} media object
 */
export function processMessage(text) {
  if ((/^#Attachments:/).test(text)) {
    const hashKey = text.split(':').pop();

    return {
      media: 'image',
      text: '',
      url: getStorageImageUrl(hashKey)
    }
  }

  return {
    media: 'text',
    text
  };
}

/**
 * Send a image through the message stream
 * @param {array} seniors - a list of seniors
 * @param {string} seniorHash - the hashed senior id
 * @returns {object} the selected senior
 */
export function getSeniorByHash(seniors, seniorHash) {
  return seniors.find((it) => hash(it.prof_id) === seniorHash);
}
